import MovementAccountSelect from "../components/MovementAccountSelect";
import CurrencySelect from "../components/CurrencySelect";
import FXPreview from "../components/FXPreview";
import FXDetails from "../components/FXDetails";
import { useState, useEffect, useMemo } from "react";
import { useOutletContext, useNavigate, useSearchParams } from "react-router-dom";
import { api, useAuth } from "../App";
import { useTransactionsData } from "../hooks/useTransactionsData";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { CurrencyInput } from "../components/ui/currency-input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { toast } from "@/lib/toast";
import { getApiErrorMessage } from "@/lib/apiError";
import { formatMoney } from "@/lib/currency";
import { Plus, Filter, TrendingUp, TrendingDown, ArrowRight, Trash2, Loader2, CalendarDays, X, Pencil, Download, FileSpreadsheet, FileText, Search, SlidersHorizontal } from "lucide-react";

// Helper to get relative date string (Today, Yesterday, or formatted date)
const getRelativeDateHeader = (dateStr) => {
  const date = new Date(dateStr);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const isToday = date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  const isYesterday = date.getDate() === yesterday.getDate() && date.getMonth() === yesterday.getMonth() && date.getFullYear() === yesterday.getFullYear();
  
  const options = { month: 'short', day: 'numeric' };
  const formattedMsg = date.toLocaleDateString('es-CO', options);
  
  if (isToday) return `Hoy, ${formattedMsg}`;
  if (isYesterday) return `Ayer, ${formattedMsg}`;
  return formattedMsg;
};

const getLocalDateInputValue = (value = new Date()) => {
  const date = new Date(value);
  date.setMinutes(date.getMinutes() - date.getTimezoneOffset());
  return date.toISOString().slice(0, 10);
};

function getCatIconUrl(name) {
  const norm = (name || "").toLowerCase().trim();
  if (norm.includes("aliment") || norm.includes("comida")) return "/Iconos/Categorias/Alimnetacion.png";
  if (norm.includes("transporte") || norm.includes("carro") || norm.includes("moto") || norm.includes("uber")) return "/Iconos/Categorias/transporte.png";
  if (norm.includes("entretenimiento") || norm.includes("diversion") || norm.includes("cine")) return "/Iconos/Categorias/Entretenimiento.png";
  if (norm.includes("salud") || norm.includes("medica") || norm.includes("farmacia")) return "/Iconos/Categorias/salud.png";
  if (norm.includes("educac") || norm.includes("universidad") || norm.includes("colegio") || norm.includes("escuela") || norm.includes("curso")) return "/Iconos/Categorias/Educacion.png";
  if (norm.includes("hogar") || norm.includes("casa") || norm.includes("muebles")) return "/Iconos/Categorias/Hogar.png";
  if (norm.includes("servicios") || norm.includes("luz") || norm.includes("agua") || norm.includes("gas")) return "/Iconos/Categorias/Servicios.png";
  if (norm.includes("suscri") || norm.includes("netflix") || norm.includes("spotify")) return "/Iconos/Categorias/Suscripciones.png";
  if (norm.includes("viaje") || norm.includes("vuelo") || norm.includes("hotel")) return "/Iconos/Categorias/Viajes.png";
  if (norm.includes("deuda") || norm.includes("prestam") || norm.includes("banco")) return "/Iconos/Categorias/Deudas.png";
  if (norm.includes("ahorro") || norm.includes("meta") || norm.includes("piggy")) return "/Iconos/Categorias/Ahorros.png";
  if (norm.includes("deporte") || norm.includes("gym") || norm.includes("ejercicio") || norm.includes("futbol") || norm.includes("entreno")) return "/Iconos/Categorias/deporte.png";
  return "/Iconos/Categorias/otros.png";
}

function getCatIcon(icon, name="", size=24) {
  if (icon && typeof icon === "string" && (icon.startsWith("/") || icon.includes(".png"))) {
    return <img src={icon} alt={name || "category"} style={{ width: size, height: size, objectFit: "contain" }} />;
  }
  const target = name || icon || "";
  if (target && typeof target === "string" && (target.startsWith("/") || target.includes(".png"))) {
    return <img src={target} alt={name || "category"} style={{ width: size, height: size, objectFit: "contain" }} />;
  }
  const url = getCatIconUrl(target);
  return <img src={url} alt={target} style={{ width: size, height: size, objectFit: "contain" }} />;
}

const TransactionsPage = () => {
  const { user } = useAuth();
  const [paymentCards, setPaymentCards] = useState([]);
  const [paymentAccounts, setPaymentAccounts] = useState([]);
  const [paymentPreferences, setPaymentPreferences] = useState({});
  const [paymentSourcesLoading, setPaymentSourcesLoading] = useState(false);
  const [paymentSourcesFailed, setPaymentSourcesFailed] = useState(false);
  const navigate = useNavigate();
  const { isExpired } = useOutletContext() || {};
  const userCurrency = user?.currency || "COP";

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 900);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  
  const [isNewCategory, setIsNewCategory] = useState(false);
  const [formCategoryOpen, setFormCategoryOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Filters
  const [searchTerm, setSearchTerm] = useState("");
  const [searchParams] = useSearchParams();
  const [filterCategory, setFilterCategory] = useState(searchParams.get("category") || "all");
  const requestedType = searchParams.get("type");
  const [filterType, setFilterType] = useState(
    requestedType === "income" || requestedType === "expense" ? requestedType : "all"
  );
  const [dateRange, setDateRange] = useState({ from: null, to: null });
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const [dateFilterType, setDateFilterType] = useState(searchParams.get("category") ? "all" : "this_month");
  
  const [editingTransaction, setEditingTransaction] = useState(null);
  const [txToDelete, setTxToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Downloads
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [downloadDateRange, setDownloadDateRange] = useState({ from: null, to: null });
  const [downloadDatePickerOpen, setDownloadDatePickerOpen] = useState(false);
  
  const [formData, setFormData] = useState({
    amount: "",
    category: "",
    description: "",
    type: "expense",
    currency: userCurrency,
    transaction_date: getLocalDateInputValue()
  });

  useEffect(() => {
    if (!dialogOpen) return;
    let active = true;
    setPaymentSourcesLoading(true);
    setPaymentSourcesFailed(false);
    Promise.all([api.get("/accounts"), api.get("/credit-cards")]).then(([accounts, cards]) => {
      if (!active) return;
      setPaymentAccounts(accounts.data?.accounts || []);
      setPaymentPreferences(accounts.data?.preferences || {});
      setPaymentCards(Array.isArray(cards.data) ? cards.data : []);
      if (!editingTransaction) setFormData(current => {
        if (current.account_id || (current.payment_method && current.payment_method !== "cash")) return current;
        const prefs = accounts.data?.preferences || {};
        const ident = prefs[current.type === "income" ? "default_income_account_id" : "default_expense_account_id"];
        const preferred = accounts.data?.accounts?.find(account => account.id === ident && !account.archived);
        return preferred ? { ...current, account_id: preferred.id, payment_method: "cash" } : current;
      });
    }).catch(() => { if (active) setPaymentSourcesFailed(true); })
      .finally(() => { if (active) setPaymentSourcesLoading(false); });
    return () => { active = false; };
  }, [dialogOpen, editingTransaction]);

  const MONTH_NAMES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

  const getDateLabel = () => {
    if (dateFilterType === "all") return "Todos";
    if (dateFilterType === "this_month") return "Este mes";
    if (dateFilterType === "last_month") return "Mes pasado";
    if (dateFilterType === "custom" && dateRange.from && dateRange.to) {
      return `${dateRange.from.toLocaleDateString("es-CO",{day:"numeric",month:"short"})} - ${dateRange.to.toLocaleDateString("es-CO",{day:"numeric",month:"short"})}`;
    }
    return "Personalizado";
  };

  const getDateFilterRange = () => {
    const today = new Date();
    switch (dateFilterType) {
      case "this_month":
        return {
          from: new Date(today.getFullYear(), today.getMonth(), 1),
          to: new Date(today.getFullYear(), today.getMonth() + 1, 0)
        };
      case "last_month":
        return {
          from: new Date(today.getFullYear(), today.getMonth() - 1, 1),
          to: new Date(today.getFullYear(), today.getMonth(), 0)
        };
      case "this_year":
        return {
          from: new Date(today.getFullYear(), 0, 1),
          to: new Date(today.getFullYear(), 11, 31)
        };
      case "custom":
        return dateRange;
      default:
        return { from: null, to: null };
    }
  };

  useEffect(() => {
    const cat = searchParams.get("category");
    const type = searchParams.get("type");
    setFilterCategory(cat || "all");
    setFilterType(type === "income" || type === "expense" ? type : "all");
  }, [searchParams]);

  useEffect(() => {
    const action = searchParams.get("action");
    if (!action || isExpired) return;

    if (action === "add-income" || action === "add-expense") {
      const type = action === "add-income" ? "income" : "expense";
      setEditingTransaction(null);
      setIsNewCategory(false);
      setFormData({
        amount: "",
        category: type === "income" ? "Salario" : "",
        description: "",
        type,
        currency: userCurrency,
        transaction_date: getLocalDateInputValue(),
        account_id: searchParams.get("account") || undefined,
      });
      setDialogOpen(true);
      navigate("/dashboard/transactions", { replace: true });
    }
  }, [searchParams, isExpired, navigate, userCurrency]);

  const range = getDateFilterRange();
  const {
    transactions, loading, loadingMore, hasMore, error, summary, summaryLoading,
    summaryError, categories, categoryDict, fetchData, refreshCategories,
  } = useTransactionsData(api, {
    category: filterCategory,
    type: filterType,
    startDate: range.from?.toISOString(),
    endDate: range.to?.toISOString(),
    userId: user?.id,
  });

  useEffect(() => {
    const handleScroll = () => {
      const scrollHeight = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight
      );
      const scrollTop = Math.max(
        window.scrollY,
        document.documentElement.scrollTop,
        document.body.scrollTop
      );
      const clientHeight = window.innerHeight;

      if (
        scrollTop + clientHeight >= scrollHeight - 250 &&
        hasMore &&
        !loading &&
        !loadingMore
      ) {
        fetchData(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [hasMore, loading, loadingMore, transactions.length, fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (paymentSourcesLoading) return;
    if (!formData.amount || (formData.type === "expense" && !formData.category)) return toast.error("Completa los campos requeridos");
    if (formData.transaction_date > getLocalDateInputValue()) {
      return toast.error("La fecha del movimiento no puede estar en el futuro");
    }
    setSubmitting(true);
    try {
      const amount = formData.type === "expense" ? -Math.abs(parseFloat(formData.amount)) : Math.abs(parseFloat(formData.amount));
      if (editingTransaction) {
        await api.put(`/transactions/${editingTransaction.id}`, {
          amount: Math.abs(parseFloat(formData.amount)),
          category: formData.category,
          description: formData.description,
          type: formData.type,
          currency: formData.currency,
          transaction_date: formData.transaction_date,
          exchange_rates: formData.exchange_rates,
          account_id: formData.account_id || "unassigned",
          actual_card_amount: formData.actual_card_amount ? Number(formData.actual_card_amount) : undefined
        });
        toast.success("Transacción actualizada");
      } else {
        await api.post("/transactions", { ...formData, amount });
        toast.success("Transacción registrada");
      }
      setDialogOpen(false);
      setEditingTransaction(null);
      setIsNewCategory(false);
      setFormData({ amount: "", category: "", description: "", type: "expense", currency: userCurrency, transaction_date: getLocalDateInputValue() });
      refreshCategories();
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, editingTransaction ? "Error al actualizar" : "Error al crear"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (tx) => {
    setEditingTransaction(tx);
    setFormData({
      amount: Math.abs(tx.fx_version && !tx.trip_id ? tx.original_amount : tx.amount).toString(),
      category: tx.category,
      description: tx.description || "",
      type: tx.type,
      trip_id: tx.trip_id,
      currency: (tx.fx_version && !tx.trip_id ? tx.original_currency : tx.currency) || userCurrency,
      payment_method: tx.payment_method || "cash",
      account_id: tx.account_id || "unassigned",
      account_name: tx.account_name,
      credit_card_name: tx.credit_card_name,
      actual_card_amount: "",
      transaction_date: getLocalDateInputValue(tx.created_at || tx.date)
    });
    setDialogOpen(true);
  };

  const handleDialogClose = (open) => {
    setDialogOpen(open);
    if (!open) {
      setEditingTransaction(null);
      setIsNewCategory(false);
      setFormCategoryOpen(false);
      setFormData({ amount: "", category: "", description: "", type: "expense", currency: userCurrency, transaction_date: getLocalDateInputValue() });
    }
  };

  const handleDelete = async () => {
    if (!txToDelete) return;
    setIsDeleting(true);
    try {
      if (txToDelete.linked_type === "subscription" && txToDelete.linked_id) {
        const { data } = await api.get(`/subscriptions/${txToDelete.linked_id}/payments`);
        const payment = (data.payments || []).find(p => p.transaction_id === txToDelete.id);
        if (!payment?.can_cancel) {
          toast.error(payment?.cancellation_error || "No pude identificar el pago. Revisa el historial en Suscripciones.");
          return;
        }
        await api.delete(`/subscriptions/${txToDelete.linked_id}/payments/${payment.id}`);
        toast.success("Pago anulado y saldo actualizado");
      } else {
        await api.delete(`/transactions/${txToDelete.id}`);
        toast.success("Transacción eliminada");
      }
      setTxToDelete(null);
      fetchData();
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al eliminar"));
    } finally {
      setIsDeleting(false);
    }
  };

  const formatCurrency = (amount) => {
    return formatMoney(amount, userCurrency, { absolute: true });
  };

  const isCreditCardExpense = (tx) => tx?.type === "expense" && tx?.payment_method && tx.payment_method !== "cash";

  const handleDownloadStatement = async (format) => {
    if (!downloadDateRange.from || !downloadDateRange.to) return toast.error("Selecciona un rango de fechas");
    setDownloading(true);
    try {
      const startDate = downloadDateRange.from.toISOString().split('T')[0];
      const endDate = downloadDateRange.to.toISOString().split('T')[0];
      const response = await api.get(`/transactions/statement?start_date=${startDate}&end_date=${endDate}&format=${format}`, {
        responseType: 'blob'
      });
      const blob = new Blob([response.data], { 
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `extracto_${startDate}_${endDate}.${format === 'pdf' ? 'pdf' : 'xlsx'}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Extracto descargado`);
      setDownloadDialogOpen(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Error al descargar"));
    } finally {
      setDownloading(false);
    }
  };
  
  const setQuickDateRange = (type) => {
    const today = new Date();
    let from, to;
    switch (type) {
      case 'this_month':
        from = new Date(today.getFullYear(), today.getMonth(), 1);
        to = new Date(today.getFullYear(), today.getMonth() + 1, 0); break;
      case 'last_month':
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to = new Date(today.getFullYear(), today.getMonth(), 0); break;
      case 'last_3_months':
        from = new Date(today.getFullYear(), today.getMonth() - 2, 1);
        to = new Date(today.getFullYear(), today.getMonth() + 1, 0); break;
      case 'this_year':
        from = new Date(today.getFullYear(), 0, 1);
        to = new Date(today.getFullYear(), 11, 31); break;
      default: return;
    }
    setDownloadDateRange({ from, to });
  };

  const lastExpense = useMemo(() => transactions.find(t => t.type === 'expense'), [transactions]);
  const lastIncome = useMemo(() => transactions.find(t => t.type === 'income'), [transactions]);

  const filteredTransactions = useMemo(() => {
     let data = transactions;
     if (searchTerm.trim()) {
        const lower = searchTerm.toLowerCase();
        data = data.filter(t => 
          (t.description || "").toLowerCase().includes(lower) || 
          (t.category || "").toLowerCase().includes(lower)
        );
     }
     return data;
  }, [transactions, searchTerm]);

  const groupedTransactions = useMemo(() => {
     const groups = {};
     filteredTransactions.forEach(tx => {
         const date = new Date(tx.created_at || tx.date);
         const dateKey = `${date.getFullYear()}-${date.getMonth()+1}-${date.getDate()}`;
         if(!groups[dateKey]) {
             groups[dateKey] = {
                 header: getRelativeDateHeader(date.toISOString()),
                 items: [],
                 sortDate: date
             };
         }
         groups[dateKey].items.push(tx);
     });
     return Object.values(groups).sort((a, b) => b.sortDate - a.sortDate);
  }, [filteredTransactions]);


  return (
    <div className="pb-32 md:pb-12 max-w-6xl w-full mx-auto" data-testid="transactions-page" style={{ fontFamily: "'Manrope', sans-serif" }}>
      <h2 style={{
        fontFamily: "'Manrope', sans-serif",
        fontWeight: 700,
        fontSize: "24px",
        lineHeight: "33px",
        color: "#255E99",
        marginBottom: "24px",
        textAlign: "left"
      }}>
        Mis movimientos
      </h2>

      {/* New Prominent Cards: Gastos & Ingresos */}
      <div className="aorra-transactions-summary" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: isMobile ? "12px" : "24px", width: "100%", marginBottom: "30px" }}>
        {/* Gastos Card */}
        <div style={{
          order: 2,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "center",
          padding: isMobile ? "12px 14px" : "9px 30px",
          gap: isMobile ? "12px" : "30px",
          height: isMobile ? "auto" : "123px",
          minHeight: isMobile ? "170px" : "auto",
          background: "#FFFFFF",
          boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
          borderRadius: "20px",
          boxSizing: "border-box",
        }}>
          {/* Icon */}
          <div style={{
            width: isMobile ? "46px" : "80px",
            height: isMobile ? "46px" : "80px",
            borderRadius: "50%",
            background: "#F0FDFD",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}>
            <img src="/Iconos/Dashboard/gastos.png" alt="Gastos" style={{ width: isMobile ? "28px" : "49.23px", height: isMobile ? "28px" : "49.23px", objectFit: "contain" }} />
          </div>
          {/* Content */}
          <div style={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: "4px", width: "100%" }}>
            <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: isMobile ? "15px" : "18px", lineHeight: "25px", color: "#47484C", textAlign: "left" }}>Gastos</span>
            <div style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? "6px" : "16px",
              width: "100%"
            }}>
              <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: isMobile ? "18px" : "24px", lineHeight: isMobile ? "24px" : "32px", color: "#255E99", whiteSpace: "nowrap" }}>
                {summaryLoading ? "…" : summaryError ? "—" : formatCurrency(summary?.total_expenses ?? summary?.month_expenses ?? 0)}
              </span>
              {/* Trend Pill */}
              {!summaryLoading && !summaryError && summary?.period === "all" && (
                <span className="text-sm text-slate-500">Total histórico</span>
              )}
              {!summaryLoading && !summaryError && summary?.period !== "all" && (() => {
                const hasIncreased = (summary?.expense_trend || 0) >= 0;
                const pillBg = hasIncreased ? "#FDF2F2" : "#F0FDFD";
                const pillColor = hasIncreased ? "#EC5F5F" : "#62BF92";
                return (
                  <div style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: isMobile ? "2px 8px" : "4px 12px",
                    gap: "4px",
                    background: pillBg,
                    borderRadius: "15px",
                    height: isMobile ? "20px" : "25px"
                  }}>
                    {hasIncreased ? (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={pillColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="19" x2="12" y2="5"></line>
                        <polyline points="5 12 12 5 19 12"></polyline>
                      </svg>
                    ) : (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={pillColor} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <polyline points="19 12 12 19 5 12"></polyline>
                      </svg>
                    )}
                    <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: isMobile ? "10px" : "14px", color: pillColor, whiteSpace: "nowrap" }}>
                      {hasIncreased ? "+" : ""}{(summary?.expense_trend || 0).toFixed(0)}% vs mes anterior
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* Ingresos Card */}
        <div style={{
          order: 1,
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          alignItems: isMobile ? "flex-start" : "center",
          padding: isMobile ? "12px 14px" : "9px 30px",
          gap: isMobile ? "12px" : "30px",
          height: isMobile ? "auto" : "123px",
          minHeight: isMobile ? "170px" : "auto",
          background: "#FFFFFF",
          boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
          borderRadius: "20px",
          boxSizing: "border-box",
        }}>
          {/* Icon */}
          <div style={{
            width: isMobile ? "46px" : "80px",
            height: isMobile ? "46px" : "80px",
            borderRadius: "50%",
            background: "#F0FDFD",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0
          }}>
            <img src="/Iconos/Dashboard/ingresos.png" alt="Ingresos" style={{ width: isMobile ? "28px" : "51.39px", height: isMobile ? "28px" : "49.52px", objectFit: "contain" }} />
          </div>
          {/* Content */}
          <div style={{ display: "flex", flexDirection: "column", flexGrow: 1, gap: "4px", width: "100%" }}>
            <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: isMobile ? "15px" : "18px", lineHeight: "25px", color: "#47484C", textAlign: "left" }}>Ingresos</span>
            <div style={{
              display: "flex",
              flexDirection: isMobile ? "column" : "row",
              alignItems: isMobile ? "flex-start" : "center",
              gap: isMobile ? "6px" : "16px",
              width: "100%"
            }}>
              <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: isMobile ? "18px" : "24px", lineHeight: isMobile ? "24px" : "32px", color: "#255E99", whiteSpace: "nowrap" }}>
                {summaryLoading ? "…" : summaryError ? "—" : formatCurrency(summary?.total_income || 0)}
              </span>
              {/* Trend Pill */}
              {!summaryLoading && !summaryError && summary?.period === "all" && (
                <span className="text-sm text-slate-500">Total histórico</span>
              )}
              {!summaryLoading && !summaryError && summary?.period !== "all" && (() => {
                const isPositive = (summary?.income_trend || 0) >= 0;
                return (
                  <div style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: isMobile ? "2px 8px" : "4px 12px",
                    gap: "4px",
                    background: isPositive ? "#F0FDFD" : "#FDF2F2",
                    borderRadius: "15px",
                    height: isMobile ? "20px" : "25px"
                  }}>
                    {isPositive ? (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#62BF92" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="19" x2="12" y2="5"></line>
                        <polyline points="5 12 12 5 19 12"></polyline>
                      </svg>
                    ) : (
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#EC5F5F" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="12" y1="5" x2="12" y2="19"></line>
                        <polyline points="19 12 12 19 5 12"></polyline>
                      </svg>
                    )}
                    <span style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 600, fontSize: isMobile ? "10px" : "14px", color: isPositive ? "#62BF92" : "#EC5F5F", whiteSpace: "nowrap" }}>
                      {isPositive ? "+" : ""}{(summary?.income_trend || 0).toFixed(0)}% vs mes anterior
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>
      {/* Search & Filter Row */}
      {isMobile ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "12px", width: "100%", marginBottom: "24px" }}>
          {/* Search */}
          <div style={{ position: "relative", width: "100%", height: "40px" }}>
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-outline w-5 h-5" style={{ color: "#ADADAD" }} />
            <input
              placeholder="Buscar transacciones..."
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                boxSizing: "border-box",
                width: "100%",
                height: "40px",
                paddingLeft: "48px",
                paddingRight: "16px",
                background: "#FFFFFF",
                border: "1px solid #CDE3F3",
                borderRadius: "12px",
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: "16px",
                color: "#4A4A4A",
                outline: "none",
              }}
            />
          </div>

          {/* Active category filter chip */}
          {filterCategory !== "all" && (
            <div style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: "0px 12px 0px 16px",
              gap: "8px",
              height: "36px",
              background: "#E1E9FC",
              borderRadius: "12px",
              width: "fit-content",
              whiteSpace: "nowrap",
            }}>
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: "13px",
                color: "#396BE9",
              }}>
                {filterCategory}
              </span>
              <button
                onClick={() => setFilterCategory("all")}
                style={{
                  width: "20px",
                  height: "20px",
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(57, 107, 233, 0.15)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                }}
              >
                <X size={10} style={{ color: "#396BE9" }} />
              </button>
            </div>
          )}

          {/* Exportar, Calendario, Filtrar in one row */}
          <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", gap: "8px", width: "100%" }}>
            {/* Exportar */}
            <button
              onClick={() => setDownloadDialogOpen(true)}
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                padding: "0px 10px",
                gap: "6px",
                height: "40px",
                background: "#E1E9FC",
                borderRadius: "12px",
                border: "none",
                color: "#396BE9",
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 700,
                fontSize: "13px",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flex: 1,
              }}
            >
              <Download className="w-4 h-4" /> Exportar
            </button>

            {/* Calendario */}
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: "0 10px 0 6px",
                    gap: 8,
                    height: 40,
                    background: "#E1E9FC",
                    borderRadius: 32,
                    border: "none",
                    cursor: "pointer",
                    boxSizing: "border-box",
                    flex: 1.2,
                    justifyContent: "center",
                  }}
                >
                  <div style={{
                    width: 28,
                    height: 28,
                    borderRadius: 100,
                    background: "#4D72F5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                  }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM19 7H5V6H19V7Z" fill="#FFFFFF"/>
                    </svg>
                  </div>
                  <span style={{
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 600,
                    fontSize: "12px",
                    color: "#396BE9",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}>
                    {getDateLabel()}
                  </span>
                  <svg width="8" height="5" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 1L5 5L9 1" stroke="#396BE9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-3xl shadow-2xl border-0 overflow-hidden" align="end"
                style={{ background:"rgba(255,255,255,0.95)", backdropFilter:"blur(24px)", zIndex: 150 }}>
                <div style={{ padding: 12 }}>
                  <div style={{ display: "flex", gap: 8, marginBottom: 12, borderBottom: "1px solid #f0f0f0", paddingBottom: 8 }}>
                    <button
                      onClick={() => { setDateFilterType("all"); setDatePickerOpen(false); }}
                      style={{
                        padding: "6px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                        fontSize: 12, fontWeight: 700, fontFamily: "'Manrope', sans-serif",
                        background: dateFilterType === "all" ? "#396BE9" : "#f5f5f5",
                        color: dateFilterType === "all" ? "#fff" : "#396BE9"
                      }}
                    >Todos</button>
                    <button
                      onClick={() => { setDateFilterType("this_month"); setDatePickerOpen(false); }}
                      style={{
                        padding: "6px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                        fontSize: 12, fontWeight: 700, fontFamily: "'Manrope', sans-serif",
                        background: dateFilterType === "this_month" ? "#396BE9" : "#f5f5f5",
                        color: dateFilterType === "this_month" ? "#fff" : "#396BE9"
                      }}
                    >Este mes</button>
                    <button
                      onClick={() => { setDateFilterType("last_month"); setDatePickerOpen(false); }}
                      style={{
                        padding: "6px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                        fontSize: 12, fontWeight: 700, fontFamily: "'Manrope', sans-serif",
                        background: dateFilterType === "last_month" ? "#396BE9" : "#f5f5f5",
                        color: dateFilterType === "last_month" ? "#fff" : "#396BE9"
                      }}
                    >Mes pasado</button>
                  </div>
                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={(r) => {
                      setDateRange(r || { from:null, to:null });
                      if (r?.from && r?.to) { setDateFilterType("custom"); setDatePickerOpen(false); }
                    }}
                    numberOfMonths={1}
                  />
                </div>
              </PopoverContent>
            </Popover>

            {/* Filtrar */}
            <Popover>
              <PopoverTrigger asChild>
                <button style={{
                  height: "40px",
                  padding: "0px 8px",
                  background: "transparent",
                  borderRadius: "12px",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 700,
                  fontSize: "13px",
                  color: "#5C5C5C",
                  display: "flex",
                  alignItems: "center",
                  gap: "4px",
                  flex: 0.8,
                  justifyContent: "center",
                }}>
                  <SlidersHorizontal className="w-4 h-4" />
                  Filtrar
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                style={{
                  background: "#FFFFFF",
                  borderRadius: "20px",
                  padding: "20px",
                  boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
                  border: "1px solid #E0EDF8",
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                  width: "280px",
                  zIndex: 150
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "14px", color: "#5C5C5C" }}>Categoría</label>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger style={{ borderRadius: "12px", border: "1px solid #CDE3F3", height: "40px" }}>
                      <SelectValue placeholder="Categoría" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-outline-variant/20 shadow-xl overflow-hidden" style={{ zIndex: 200 }}>
                      <SelectItem value="all">Todas</SelectItem>
                      {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "14px", color: "#5C5C5C" }}>Tipo</label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger style={{ borderRadius: "12px", border: "1px solid #CDE3F3", height: "40px" }}>
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-outline-variant/20 shadow-xl overflow-hidden" style={{ zIndex: 200 }}>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="income">Solo Ingresos</SelectItem>
                      <SelectItem value="expense">Solo Gastos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      ) : (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "20px", flexWrap: "wrap", marginBottom: "24px" }}>
          {/* Search */}
          <div style={{ position: "relative", flex: "1 1 300px", maxWidth: "537px", height: "40px" }}>
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-outline w-5 h-5" style={{ color: "#ADADAD" }} />
            <input
              placeholder="Buscar transacciones..."
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                boxSizing: "border-box",
                width: "100%",
                height: "40px",
                paddingLeft: "48px",
                paddingRight: "16px",
                background: "#FFFFFF",
                border: "1px solid #CDE3F3",
                borderRadius: "12px",
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: "16px",
                color: "#4A4A4A",
                outline: "none",
              }}
            />
          </div>

          {/* Active category filter chip */}
          {filterCategory !== "all" && (
            <div style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              padding: "0px 12px 0px 16px",
              gap: "8px",
              height: "40px",
              background: "#E1E9FC",
              borderRadius: "12px",
              flexShrink: 0,
              whiteSpace: "nowrap",
            }}>
              <span style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: "14px",
                color: "#396BE9",
              }}>
                {filterCategory}
              </span>
              <button
                onClick={() => setFilterCategory("all")}
                style={{
                  width: "22px",
                  height: "22px",
                  borderRadius: "50%",
                  border: "none",
                  background: "rgba(57, 107, 233, 0.15)",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: 0,
                  transition: "background 0.15s ease",
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "rgba(57, 107, 233, 0.3)"}
                onMouseLeave={(e) => e.currentTarget.style.background = "rgba(57, 107, 233, 0.15)"}
              >
                <X size={12} style={{ color: "#396BE9" }} />
              </button>
            </div>
          )}

          {/* Exportar reporte button (next to search) */}
          <button
            onClick={() => setDownloadDialogOpen(true)}
            style={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              justifyContent: "center",
              padding: "0px 16px",
              gap: "8px",
              height: "40px",
              background: "#E1E9FC",
              borderRadius: "12px",
              border: "none",
              color: "#396BE9",
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 700,
              fontSize: "14px",
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
              transition: "opacity 0.15s ease"
            }}
            onMouseEnter={(e) => e.currentTarget.style.opacity = 0.85}
            onMouseLeave={(e) => e.currentTarget.style.opacity = 1}
          >
            <Download className="w-4 h-4" /> Exportar reporte
          </button>

          {/* Date Filter & Advanced Filters Row */}
          <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
            {/* New Figma Date Filter Chip Trigger (Dashboard style) */}
            <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
              <PopoverTrigger asChild>
                <button
                  style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    padding: "0 16px 0 8px",
                    gap: 8,
                    width: "fit-content",
                    minWidth: 182,
                    height: 48,
                    background: "#E1E9FC",
                    borderRadius: 32,
                    border: "none",
                    cursor: "pointer",
                    boxSizing: "border-box",
                    transition: "opacity 0.15s ease",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.opacity = 0.9}
                  onMouseLeave={(e) => e.currentTarget.style.opacity = 1}
                >
                  {/* Calendar Icon BG Circle */}
                  <div style={{
                    width: 34,
                    height: 34,
                    borderRadius: 100,
                    background: "#4D72F5",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
                  }}>
                    {/* White Calendar SVG */}
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M19 4H18V2H16V4H8V2H6V4H5C3.89 4 3.01 4.9 3.01 6L3 20C3 21.1 3.89 22 5 22H19C20.1 22 21 21.1 21 20V6C21 4.9 20.1 4 19 4ZM19 20H5V9H19V20ZM19 7H5V6H19V7Z" fill="#FFFFFF"/>
                    </svg>
                  </div>

                  {/* Label container */}
                  <div style={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    justifyContent: "space-between",
                    flex: 1,
                    gap: 8,
                  }}>
                    <span style={{
                      fontFamily: "'Manrope', sans-serif",
                      fontWeight: 600,
                      fontSize: 16,
                      lineHeight: "25px",
                      color: "#396BE9",
                      whiteSpace: "nowrap",
                    }}>
                      {getDateLabel()}
                    </span>

                    {/* Arrow chevron */}
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: 24, height: 24 }}>
                      <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1L5 5L9 1" stroke="#396BE9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  </div>
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0 rounded-3xl shadow-2xl border-0 overflow-hidden" align="end"
                style={{ background:"rgba(255,255,255,0.95)", backdropFilter:"blur(24px)", zIndex: 150 }}>
                <div style={{ padding: 12 }}>
                  {/* Calendar quick filter buttons inside popover */}
                  <div style={{ display: "flex", gap: 8, marginBottom: 12, borderBottom: "1px solid #f0f0f0", paddingBottom: 8 }}>
                    <button
                      onClick={() => { setDateFilterType("all"); setDatePickerOpen(false); }}
                      style={{
                        padding: "6px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                        fontSize: 12, fontWeight: 700, fontFamily: "'Manrope', sans-serif",
                        background: dateFilterType === "all" ? "#396BE9" : "#f5f5f5",
                        color: dateFilterType === "all" ? "#fff" : "#396BE9"
                      }}
                    >Todos</button>
                    <button
                      onClick={() => { setDateFilterType("this_month"); setDatePickerOpen(false); }}
                      style={{
                        padding: "6px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                        fontSize: 12, fontWeight: 700, fontFamily: "'Manrope', sans-serif",
                        background: dateFilterType === "this_month" ? "#396BE9" : "#f5f5f5",
                        color: dateFilterType === "this_month" ? "#fff" : "#396BE9"
                      }}
                    >Este mes</button>
                    <button
                      onClick={() => { setDateFilterType("last_month"); setDatePickerOpen(false); }}
                      style={{
                        padding: "6px 12px", borderRadius: 8, border: "none", cursor: "pointer",
                        fontSize: 12, fontWeight: 700, fontFamily: "'Manrope', sans-serif",
                        background: dateFilterType === "last_month" ? "#396BE9" : "#f5f5f5",
                        color: dateFilterType === "last_month" ? "#fff" : "#396BE9"
                      }}
                    >Mes pasado</button>
                  </div>

                  <Calendar
                    mode="range"
                    selected={dateRange}
                    onSelect={(r) => {
                      setDateRange(r || { from:null, to:null });
                      if (r?.from && r?.to) { setDateFilterType("custom"); setDatePickerOpen(false); }
                    }}
                    numberOfMonths={1}
                  />
                </div>
              </PopoverContent>
            </Popover>

            {/* Filtrar Popover */}
            <Popover>
              <PopoverTrigger asChild>
                <button style={{
                  height: "40px",
                  padding: "4px 16px",
                  background: "transparent",
                  borderRadius: "16px",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 700,
                  fontSize: "16px",
                  color: "#5C5C5C",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px"
                }}>
                  <SlidersHorizontal className="w-4 h-4" />
                  Filtrar
                </button>
              </PopoverTrigger>
              <PopoverContent
                align="end"
                style={{
                  background: "#FFFFFF",
                  borderRadius: "20px",
                  padding: "20px",
                  boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
                  border: "1px solid #E0EDF8",
                  display: "flex",
                  flexDirection: "column",
                  gap: "14px",
                  width: "280px",
                  zIndex: 150
                }}
              >
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "14px", color: "#5C5C5C" }}>Categoría</label>
                  <Select value={filterCategory} onValueChange={setFilterCategory}>
                    <SelectTrigger style={{ borderRadius: "12px", border: "1px solid #CDE3F3", height: "40px" }}>
                      <SelectValue placeholder="Categoría" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-outline-variant/20 shadow-xl overflow-hidden" style={{ zIndex: 200 }}>
                      <SelectItem value="all">Todas</SelectItem>
                      {categories.map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <label style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "14px", color: "#5C5C5C" }}>Tipo</label>
                  <Select value={filterType} onValueChange={setFilterType}>
                    <SelectTrigger style={{ borderRadius: "12px", border: "1px solid #CDE3F3", height: "40px" }}>
                      <SelectValue placeholder="Tipo" />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl border-outline-variant/20 shadow-xl overflow-hidden" style={{ zIndex: 200 }}>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="income">Solo Ingresos</SelectItem>
                      <SelectItem value="expense">Solo Gastos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      )}

      {summaryError && <p role="status" className="mb-4 text-sm text-slate-500">{summaryError}</p>}

      {/* Main Table Card (latest transactions) */}
      <div style={{
        background: "#FFFFFF",
        boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
        borderRadius: "20px",
        padding: "30px 25px",
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        width: "100%"
      }}>
        {/* Header of Table */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%", position: "relative", height: "54px", marginBottom: "8px" }}>
          <h3 style={{ fontFamily: "'Manrope', sans-serif", fontWeight: 700, fontSize: "18px", color: "#2B50AF", margin: 0 }}>
            Movimientos{filterCategory !== "all" ? ` - ${filterCategory}` : ""}
          </h3>
          {groupedTransactions.length > 0 && (
            <button
              onClick={() => !isExpired && setDialogOpen(true)}
              disabled={isExpired}
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                padding: "0px 16px",
                gap: "6px",
                width: "auto",
                height: "40px",
                background: "#396BE9",
                borderRadius: "12px",
                border: "none",
                cursor: "pointer",
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 600,
                fontSize: "14px",
                color: "#FFFFFF"
              }}
            >
              <Plus size={16} strokeWidth={3} />
              Agregar movimiento
            </button>
          )}
        </div>

        {/* Transactions List */}
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          {error && (
            <div role="alert" className="flex items-center justify-between gap-4 rounded-xl bg-red-50 p-4 text-sm text-red-700">
              <span>{error}</span>
              <button type="button" onClick={() => fetchData()} className="font-bold underline">Reintentar</button>
            </div>
          )}
          {loading ? (
            <div role="status" className="flex items-center justify-center gap-3 py-20 text-primary">
              <Loader2 className="h-5 w-5 animate-spin" /> Cargando movimientos…
            </div>
          ) : groupedTransactions.length > 0 ? (
            groupedTransactions.map((group, groupIdx) => (
              <div key={groupIdx}>
                {/* Header de grupo de fecha */}
                <div style={{ padding: "16px 0 8px", textAlign: "left" }}>
                  <span style={{ fontSize: "11px", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#8E8E8E" }}>
                     {group.header}
                  </span>
                </div>
                
                {group.items.map((tx) => (
                  <div
                    key={tx.id}
                    onClick={() => handleEdit(tx)}
                    style={{
                      boxSizing: "border-box",
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 0px",
                      gap: "12px",
                      width: "100%",
                      minHeight: "60px",
                      borderBottom: "1px solid #E0EDF8",
                      cursor: "pointer",
                      transition: "background 0.2s"
                    }}
                    className="hover:bg-slate-50/50"
                  >
                    {/* Left content (icon + name) */}
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", flexGrow: 1, minWidth: 0 }}>
                      {/* Circular icon background */}
                      <div style={{
                        width: "38px",
                        height: "38px",
                        borderRadius: "50%",
                        background: "#F6F8FD",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0
                      }}>
                        {getCatIcon(tx.icon, tx.category, 20)}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", minWidth: 0, gap: "2px" }}>
                        <span style={{
                          fontFamily: "'Manrope', sans-serif",
                          fontWeight: 500,
                          fontSize: "15px",
                          lineHeight: "20px",
                          color: "#000000",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis"
                        }}>
                          {tx.description || tx.category}
                        </span>
                        <FXDetails transaction={tx} />
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
                        {tx.trip_id && (
                          <span title={`Viaje: ${tx.trip_name || tx.category}`} style={{
                            maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            padding: "1px 6px", borderRadius: 999, background: "#EAF7F2", color: "#23765C",
                            fontSize: 10, lineHeight: "15px", fontWeight: 700,
                          }}>✈ Viaje: {tx.trip_name || tx.category}</span>
                        )}
                        {isCreditCardExpense(tx) && (
                          <span style={{
                            width: "fit-content",
                            padding: "1px 6px",
                            borderRadius: 999,
                            background: "#EEF4FF",
                            color: "#396BE9",
                            fontSize: 9,
                            lineHeight: "13px",
                            fontWeight: 800,
                          }}>
                            💳 Gasto con tarjeta
                          </span>
                        )}
                        </div>
                      </div>
                    </div>

                    {/* Right content (date + amount) */}
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "2px", flexShrink: 0 }}>
                      <span style={{
                        fontFamily: "'Quicksand', sans-serif",
                        fontWeight: 500,
                        fontSize: "11px",
                        lineHeight: "14px",
                        color: "#8E8E8E"
                      }}>
                        {new Date(tx.created_at).toLocaleDateString("es-CO", { day: "numeric", month: "numeric", year: "2-digit" })}
                      </span>
                      <span style={{
                        fontFamily: "'Manrope', sans-serif",
                        fontWeight: 700,
                        fontSize: "14px",
                        lineHeight: "18px",
                        color: "#255E99"
                      }}>
                        {tx.type === "income" ? "+" : "-"} {formatCurrency(tx.amount)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ))
          ) : !error && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "60px 40px",
              background: "#FFFFFF",
              borderRadius: "20px",
              width: "100%",
              boxSizing: "border-box"
            }}>
              {/* SVG Illustration - Empty inbox (Figma spec) */}
              <div style={{ position: "relative", width: "250px", height: "200px", marginBottom: "24px" }}>
                <svg width="250" height="200" viewBox="0 0 250 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                  {/* tile_background - White base */}
                  <rect width="250" height="200" fill="white" />

                  {/* Background stain - main cloud shape behind icon (#F3F7FF) */}
                  {/* Figma: left 6.8%, right 8.4%, top 32%, bottom 33% → x:17 w:212 y:64 h:70 */}
                  <path d="M38 98C28 98 17 94 17 88C17 78 32 68 52 68L60 68C65 64 78 64 90 64L160 64C172 64 185 64 190 68L198 68C218 68 233 78 233 88C233 94 222 98 212 98L206 100C206 108 204 118 198 126C194 132 186 134 178 134L160 134C148 138 132 138 118 138L90 138C82 138 74 134 70 130C64 124 56 112 52 104L46 100C42 100 38 98 38 98Z" fill="#F3F7FF" />

                  {/* Left pill extension */}
                  <rect x="10" y="88" width="50" height="18" rx="9" fill="#F3F7FF" />

                  {/* Right pill extension */}
                  <rect x="195" y="84" width="48" height="16" rx="8" fill="#F3F7FF" />

                  {/* Small oval accent - right side (#E7F6FF) */}
                  <ellipse cx="219" cy="120" rx="14" ry="8" fill="#E7F6FF" />

                  {/* ── Inbox tray ── */}
                  {/* Interior fill - trapezoid behind the box (#E8F0FE) */}
                  <path d="M93 90L103 72H147L157 90H143C143 90 140 102 125 102C110 102 107 90 107 90H93Z" fill="#E8F0FE" />

                  {/* Back panel of inbox - white fill */}
                  <path d="M84 112L93 90H157L166 112C166 115 164 118 160 118H90C86 118 84 115 84 112Z" fill="white" />

                  {/* Front panel interior fill - white */}
                  <rect x="86" y="96" width="78" height="22" rx="2" fill="white" />

                  {/* Inbox box outline (#1F64E7, 2.5px) */}
                  <rect x="85" y="82" width="80" height="36" rx="3" stroke="#1F64E7" strokeWidth="2.5" fill="none" />

                  {/* Trapezoid top flap - open lid */}
                  <path d="M85 82L97 62H153L165 82" stroke="#1F64E7" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />

                  {/* Inbox shelf with curved notch */}
                  <path d="M85 96H107C107 96 110 108 125 108C140 108 143 96 143 96H165" stroke="#1F64E7" strokeWidth="2.5" strokeLinecap="round" fill="none" />

                  {/* ── Sparkle lines (#75A4FE, 2.5px) ── */}
                  {/* Center line - tallest */}
                  <line x1="125" y1="38" x2="125" y2="52" stroke="#75A4FE" strokeWidth="2.5" strokeLinecap="round" />
                  {/* Left line */}
                  <line x1="112" y1="42" x2="108" y2="54" stroke="#75A4FE" strokeWidth="2.5" strokeLinecap="round" />
                  {/* Right line */}
                  <line x1="138" y1="42" x2="142" y2="54" stroke="#75A4FE" strokeWidth="2.5" strokeLinecap="round" />
                </svg>
              </div>

              {/* Text */}
              <h4 style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 800,
                fontSize: "18px",
                lineHeight: "25px",
                color: "#2B2B2B",
                margin: "0 0 6px 0",
                textAlign: "center"
              }}>
                Sin resultados
              </h4>
              <p style={{
                fontFamily: "'Manrope', sans-serif",
                fontWeight: 500,
                fontSize: "14px",
                lineHeight: "20px",
                color: "#8E8E8E",
                margin: "0 0 28px 0",
                textAlign: "center"
              }}>
                No hay movimientos registrados
              </p>

              {/* Agregar movimiento button */}
              <button
                onClick={() => !isExpired && setDialogOpen(true)}
                disabled={isExpired}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 28px",
                  gap: "8px",
                  height: "44px",
                  background: "#5B7EF5",
                  borderRadius: "12px",
                  border: "none",
                  cursor: isExpired ? "not-allowed" : "pointer",
                  fontFamily: "'Manrope', sans-serif",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#FFFFFF",
                  transition: "opacity 0.15s ease, transform 0.15s ease",
                  boxShadow: "0 4px 14px rgba(91, 126, 245, 0.3)"
                }}
                onMouseEnter={(e) => { e.currentTarget.style.opacity = 0.9; e.currentTarget.style.transform = "translateY(-1px)"; }}
                onMouseLeave={(e) => { e.currentTarget.style.opacity = 1; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <Plus size={18} strokeWidth={2.5} />
                Agregar movimiento
              </button>
            </div>
          )}

          {/* Infinite Scroll Footer Indicators */}
          {groupedTransactions.length > 0 && (
            <div style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "20px 0 10px",
              borderTop: "1px solid #E0EDF8",
              marginTop: "10px"
            }}>
              {loadingMore && (
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <Loader2 className="w-5 h-5 animate-spin text-primary" />
                  <span style={{ fontFamily: "'Manrope', sans-serif", fontSize: "14px", color: "#8E8E8E" }}>Cargando más movimientos...</span>
                </div>
              )}

              {!loadingMore && hasMore && (
                <button
                  onClick={() => fetchData(false)}
                  style={{
                    background: "transparent",
                    border: "none",
                    color: "#396BE9",
                    fontFamily: "'Manrope', sans-serif",
                    fontWeight: 700,
                    fontSize: "14px",
                    cursor: "pointer",
                    padding: "8px 16px",
                    borderRadius: "8px",
                    transition: "background 0.2s"
                  }}
                  className="hover:bg-slate-50"
                >
                  Cargar más movimientos
                </button>
              )}

              {!loadingMore && !hasMore && !error && (
                <span style={{
                  fontFamily: "'Manrope', sans-serif",
                  fontSize: "13px",
                  color: "#ADADAD",
                  fontWeight: 600
                }}>
                  Has llegado al final de tus movimientos
                </span>
              )}
            </div>
          )}
        </div>
      </div>


      <Dialog open={dialogOpen} onOpenChange={(open) => (!open || !isExpired) && handleDialogClose(open)}>
         <DialogContent
           hideClose
           className="p-0 border-0 movement-dialog"
           aria-describedby={undefined}
           style={{
             width: "calc(100vw - 32px)",
             maxWidth: "522px",
             maxHeight: "calc(100dvh - 32px)",
             overflowY: "auto",
             overflowX: "hidden",
             overscrollBehavior: "contain",
             background: "#FFFFFF",
             boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
             borderRadius: "20px",
             padding: "24px",
           }}
         >
           <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "20px", width: "100%", minWidth: 0, position: "relative" }}>
           {/* Close X button */}
           <button
             aria-label="Cerrar formulario"
             onClick={() => handleDialogClose(false)}
             style={{
               position: "absolute",
               top: "0px",
               right: "0px",
               width: "24px",
               height: "24px",
               background: "none",
               border: "none",
               cursor: "pointer",
               display: "flex",
               alignItems: "center",
               justifyContent: "center",
               padding: 0,
             }}
           >
             <X size={18} style={{ color: "#255E99", strokeWidth: 2.5 }} />
           </button>

           {/* Title */}
           <DialogTitle style={{
             width: "100%",
             fontFamily: "'Manrope', sans-serif",
             fontWeight: 700,
             fontSize: "22px",
             lineHeight: "28px",
             padding: "0 28px",
             textAlign: "center",
             color: "#1B2559",
             margin: 0,
           }}>
             {editingTransaction ? "Editar movimiento" : "Registrar nuevo movimiento"}
           </DialogTitle>

           {/* Type Toggle */}
           <div style={{
             display: "flex",
             flexDirection: "row",
             alignItems: "center",
             padding: "4px",
             width: "100%",
             maxWidth: "395px",
             height: "48px",
             background: "#FFFFFF",
             boxShadow: "-2px 0px 20px rgba(176, 179, 189, 0.25)",
             borderRadius: "16px",
             boxSizing: "border-box",
           }}>
             <button
               type="button"
               onClick={() => setFormData({...formData, type: "expense"})}
               style={{
                 flex: 1,
                 height: "40px",
                 borderRadius: "16px",
                 border: "none",
                 cursor: "pointer",
                 fontFamily: "'Manrope', sans-serif",
                 fontWeight: 700,
                 fontSize: "16px",
                 lineHeight: "32px",
                 letterSpacing: "-0.018em",
                 background: formData.type === "expense" ? "#396BE9" : "transparent",
                 color: formData.type === "expense" ? "#FFFFFF" : "#4A4A4A",
                 opacity: formData.type === "expense" ? 1 : 0.8,
                 transition: "all 0.2s ease",
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "center",
                 gap: "6px",
               }}
             >
               😵‍💫 Gasto
             </button>
             <button
               type="button"
               disabled={Boolean(formData.trip_id) || Boolean(editingTransaction && formData.payment_method && formData.payment_method !== "cash")}
               onClick={() => setFormData({...formData, type: "income", category: formData.category || "Salario", payment_method: "cash", account_id: formData.payment_method && formData.payment_method !== "cash" ? paymentPreferences.default_income_account_id || undefined : formData.account_id, exchange_rates: {} })}
               style={{
                 flex: 1,
                 height: "40px",
                 borderRadius: "16px",
                 border: "none",
                 cursor: "pointer",
                 fontFamily: "'Manrope', sans-serif",
                 fontWeight: 700,
                 fontSize: "16px",
                 lineHeight: "32px",
                 letterSpacing: "-0.018em",
                 background: formData.type === "income" ? "#396BE9" : "transparent",
                 color: formData.type === "income" ? "#FFFFFF" : "#4A4A4A",
                 opacity: formData.type === "income" ? 1 : 0.8,
                 transition: "all 0.2s ease",
                 display: "flex",
                 alignItems: "center",
                 justifyContent: "center",
                 gap: "6px",
               }}
             >
               🤑 Ingreso
             </button>
           </div>

           {/* Form Fields */}
           <form onSubmit={handleSubmit} style={{
             display: "flex",
             flexDirection: "column",
             alignItems: "center",
             gap: "16px",
             width: "100%",
             maxWidth: "398px",
           }}>
             <CurrencySelect value={formData.currency} portalCurrency={userCurrency} disabled={Boolean(editingTransaction?.trip_id)} onChange={currency => setFormData({ ...formData, currency, exchange_rates: {} })} label="Moneda del movimiento" />
             <MovementAccountSelect accounts={paymentAccounts} cards={paymentCards} value={{ ...formData, original_account_id: editingTransaction?.account_id }}
               editing={Boolean(editingTransaction)} loading={paymentSourcesLoading} failed={paymentSourcesFailed}
               onChange={(selection, source) => setFormData({ ...formData, ...selection, exchange_rates: {}, ...(!editingTransaction && source ? { currency: source.currency } : {}) })} />
             {editingTransaction?.credit_card_amount != null && !editingTransaction?.trip_id && <label className="text-sm w-full">Cargo real del banco ({editingTransaction.credit_card_currency}), opcional
               <CurrencyInput currency={editingTransaction.credit_card_currency} value={formData.actual_card_amount || ""} onChange={e => setFormData({ ...formData, actual_card_amount: e.target.value })} />
             </label>}
             {!editingTransaction && <FXPreview amount={formData.amount} currency={formData.currency || userCurrency} targets={[userCurrency, paymentCards.find(c => c.id === formData.payment_method)?.currency, paymentAccounts.find(a => a.id === formData.account_id)?.currency]} date={formData.transaction_date} rates={formData.exchange_rates} onRatesChange={exchange_rates => setFormData({ ...formData, exchange_rates })} />}
             {/* Amount field */}
             <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
               <label style={{
                 fontFamily: "'Manrope', sans-serif",
                 fontWeight: 800,
                 fontSize: "16px",
                 lineHeight: "22px",
                 color: "#5C5C5C",
               }}>
                 {formData.type === "expense" ? "¿Cuánto gastaste? 🧐" : "¿Cuánto ingresaste? 🤑"}
               </label>
               <CurrencyInput
                 id="amount"
                 currency={formData.currency || userCurrency}
                 placeholder="$0"
                 value={formData.amount}
                 onChange={(e) => setFormData({...formData, amount: e.target.value})}
                 style={{
                   boxSizing: "border-box",
                   width: "100%",
                   height: "48px",
                   padding: "12px 12px 12px 32px",
                   border: "1px solid #DDDDDD",
                   borderRadius: "20px",
                   fontFamily: "'Manrope', sans-serif",
                   fontWeight: 400,
                   fontSize: "16px",
                   lineHeight: "24px",
                   color: "#4A4A4A",
                   outline: "none",
                   background: "#FFFFFF",
                 }}
                 className=""
               />
             </div>

             {/* Category field - only for expenses */}
             {formData.type === "expense" && (
               <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
                 <label style={{
                   fontFamily: "'Manrope', sans-serif",
                   fontWeight: 800,
                   fontSize: "16px",
                   lineHeight: "22px",
                   color: "#5C5C5C",
                 }}>
                   Selecciona una categoría
                 </label>
                 {!isNewCategory ? (
                   <div style={{ position: "relative", width: "100%" }}>
                     <button
                       type="button"
                       disabled={Boolean(formData.trip_id)}
                       onClick={() => setFormCategoryOpen?.(!formCategoryOpen)}
                       style={{
                         boxSizing: "border-box",
                         width: "100%",
                         height: "48px",
                         padding: "12px",
                         border: "1px solid #DDDDDD",
                         borderRadius: "20px",
                         fontFamily: "'Manrope', sans-serif",
                         fontWeight: 400,
                         fontSize: "16px",
                         lineHeight: "24px",
                         color: formData.category ? "#4A4A4A" : "rgba(92,92,92,0.42)",
                         background: "#FFFFFF",
                         cursor: "pointer",
                         display: "flex",
                         alignItems: "center",
                         justifyContent: "space-between",
                         textAlign: "left",
                       }}
                     >
                       <span>{formData.category || "Ejem: salud"}</span>
                       <svg width="24" height="24" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                         <path d="M6 9L12 15L18 9" stroke="#396BE9" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                       </svg>
                     </button>

                     {/* Dropdown list - always opens downward */}
                     {formCategoryOpen && (
                       <>
                         {/* Invisible overlay to close */}
                         <div
                           onClick={() => setFormCategoryOpen(false)}
                           style={{ position: "fixed", inset: 0, zIndex: 199 }}
                         />
                         <div style={{
                           position: "absolute",
                           top: "calc(100% + 4px)",
                           left: 0,
                           width: "100%",
                           maxHeight: "230px",
                           overflowY: "auto",
                           background: "#FFFFFF",
                           border: "1px solid #DCDEE0",
                           borderRadius: "12px",
                           zIndex: 200,
                           display: "flex",
                           flexDirection: "column",
                         }}>
                           {categories.map(cat => (
                             <button
                               key={cat}
                               type="button"
                               onClick={() => { setFormData({...formData, category: cat}); setFormCategoryOpen(false); }}
                               style={{
                                 boxSizing: "border-box",
                                 width: "100%",
                                 height: "46px",
                                 padding: "12px 24px",
                                 border: "none",
                                 borderBottom: "1px solid #EDEDED",
                                 background: formData.category === cat ? "#E1E9FC" : "#FFFFFF",
                                 fontFamily: "'Manrope', sans-serif",
                                 fontWeight: formData.category === cat ? 700 : 400,
                                 fontSize: "16px",
                                 lineHeight: "22px",
                                 color: formData.category === cat ? "#396BE9" : "#3A434A",
                                 cursor: "pointer",
                                 textAlign: "left",
                                 transition: "background 0.12s ease",
                                 display: "flex",
                                 alignItems: "center",
                               }}
                               onMouseEnter={(e) => { if (formData.category !== cat) { e.currentTarget.style.background = "#F0F4FF"; } }}
                               onMouseLeave={(e) => { if (formData.category !== cat) { e.currentTarget.style.background = "#FFFFFF"; } }}
                             >
                               {cat}
                             </button>
                           ))}
                           {/* Nueva categoría option */}
                           <button
                             type="button"
                             onClick={() => { setIsNewCategory(true); setFormData({...formData, category: ""}); setFormCategoryOpen(false); }}
                             style={{
                               boxSizing: "border-box",
                               width: "100%",
                               height: "46px",
                               padding: "12px 24px",
                               border: "none",
                               background: "#FFFFFF",
                               fontFamily: "'Manrope', sans-serif",
                               fontWeight: 600,
                               fontSize: "16px",
                               lineHeight: "22px",
                               color: "#396BE9",
                               cursor: "pointer",
                               textAlign: "left",
                               display: "flex",
                               alignItems: "center",
                               gap: "8px",
                             }}
                             onMouseEnter={(e) => e.currentTarget.style.background = "#F0F4FF"}
                             onMouseLeave={(e) => e.currentTarget.style.background = "#FFFFFF"}
                           >
                             <Plus size={14} /> Nueva...
                           </button>
                         </div>
                       </>
                     )}
                   </div>
                 ) : (
                   <div style={{ position: "relative", width: "100%" }}>
                     <input
                       placeholder="Escribe la nueva categoría"
                       value={formData.category}
                       onChange={(e) => setFormData({...formData, category: e.target.value})}
                       autoFocus
                       style={{
                         boxSizing: "border-box",
                         width: "100%",
                         height: "48px",
                         padding: "12px 44px 12px 12px",
                         border: "1px solid #DDDDDD",
                         borderRadius: "20px",
                         fontFamily: "'Manrope', sans-serif",
                         fontWeight: 400,
                         fontSize: "16px",
                         color: "#4A4A4A",
                         outline: "none",
                         background: "#FFFFFF",
                       }}
                     />
                     <button
                       type="button"
                       onClick={() => { setIsNewCategory(false); setFormData({...formData, category: categories[0] || ""}); }}
                       style={{
                         position: "absolute",
                         right: "10px",
                         top: "50%",
                         transform: "translateY(-50%)",
                         width: "28px",
                         height: "28px",
                         borderRadius: "50%",
                         border: "none",
                         background: "rgba(57, 107, 233, 0.1)",
                         cursor: "pointer",
                         display: "flex",
                         alignItems: "center",
                         justifyContent: "center",
                         padding: 0,
                       }}
                     >
                       <X size={14} style={{ color: "#396BE9" }} />
                     </button>
                   </div>
                 )}
               </div>
             )}

             {/* Description field */}
             <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
               <label style={{
                 fontFamily: "'Manrope', sans-serif",
                 fontWeight: 800,
                 fontSize: "16px",
                 lineHeight: "22px",
                 color: "#5C5C5C",
               }}>
                 {formData.type === "expense" ? "Describe tu gasto (opcional)" : "Describe tu ingreso (opcional)"}
               </label>
               <input
                 id="description"
                 placeholder={formData.type === "expense" ? "Ejem: Medicinas" : "Ejem: Salario mensual"}
                 value={formData.description}
                 onChange={(e) => setFormData({...formData, description: e.target.value})}
                 style={{
                   boxSizing: "border-box",
                   width: "100%",
                   height: "48px",
                   padding: "12px",
                   border: "1px solid #DDDDDD",
                   borderRadius: "20px",
                   fontFamily: "'Manrope', sans-serif",
                   fontWeight: 400,
                   fontSize: "16px",
                   lineHeight: "24px",
                   color: "#4A4A4A",
                   outline: "none",
                   background: "#FFFFFF",
                 }}
               />
             </div>

             {/* Movement date — supports registering a movement that happened in a past month */}
             <div style={{ display: "flex", flexDirection: "column", gap: "10px", width: "100%" }}>
               <label style={{
                 fontFamily: "'Manrope', sans-serif",
                 fontWeight: 800,
                 fontSize: "16px",
                 lineHeight: "22px",
                 color: "#5C5C5C",
               }}>
                 Fecha del movimiento
               </label>
               <input
                 id="transaction-date"
                 type="date"
                 value={formData.transaction_date}
                 max={getLocalDateInputValue()}
                 onChange={(e) => setFormData({ ...formData, transaction_date: e.target.value })}
                 style={{
                   boxSizing: "border-box",
                   width: "100%",
                   height: "48px",
                   padding: "12px",
                   border: "1px solid #DDDDDD",
                   borderRadius: "20px",
                   fontFamily: "'Manrope', sans-serif",
                   fontWeight: 400,
                   fontSize: "16px",
                   lineHeight: "24px",
                   color: "#4A4A4A",
                   outline: "none",
                   background: "#FFFFFF",
                 }}
               />
             </div>

             {/* Action buttons */}
             <div style={{ display: "flex", gap: "12px", width: "100%", marginTop: "16px" }}>
               {editingTransaction && (
                 <button
                   type="button"
                   onClick={() => { setDialogOpen(false); setTxToDelete(editingTransaction); }}
                   style={{
                     flex: 1,
                     height: "54px",
                     borderRadius: "16px",
                     border: "none",
                     background: "rgba(239, 68, 68, 0.08)",
                     color: "#ef4444",
                     fontFamily: "'Manrope', sans-serif",
                     fontWeight: 700,
                     fontSize: "16px",
                     cursor: "pointer",
                     transition: "background 0.15s ease",
                   }}
                   onMouseEnter={(e) => e.currentTarget.style.background = "rgba(239, 68, 68, 0.15)"}
                   onMouseLeave={(e) => e.currentTarget.style.background = "rgba(239, 68, 68, 0.08)"}
                 >
                   Eliminar
                 </button>
               )}
               <button
                 type="submit"
                 disabled={submitting || paymentSourcesLoading}
                 style={{
                   flex: 1,
                   height: "54px",
                   borderRadius: "16px",
                   border: "none",
                   background: "#E1E9FC",
                   color: "#396BE9",
                   fontFamily: "'Manrope', sans-serif",
                   fontWeight: 500,
                   fontSize: "16px",
                   cursor: submitting ? "not-allowed" : "pointer",
                   opacity: submitting ? 0.6 : 1,
                   transition: "all 0.15s ease",
                   display: "flex",
                   alignItems: "center",
                   justifyContent: "center",
                   gap: "8px",
                 }}
                 onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.background = "#D4DFFA"; }}
                 onMouseLeave={(e) => { if (!submitting) e.currentTarget.style.background = "#E1E9FC"; }}
               >
                 {submitting ? <Loader2 size={20} style={{ animation: "spin 1s linear infinite" }} /> : (editingTransaction ? "Guardar cambios" : "Guardar cambios")}
               </button>
             </div>
           </form>
           </div>
         </DialogContent>
      </Dialog>
      
      <Dialog open={!!txToDelete} onOpenChange={(open) => !open && setTxToDelete(null)}>
         <DialogContent className="sm:max-w-md bg-surface-container-lowest border-outline-variant/20 rounded-3xl p-8 shadow-2xl">
           <DialogHeader>
             <div className="w-16 h-16 bg-rose-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <Trash2 className="w-8 h-8 text-rose-500" />
             </div>
             <DialogTitle className="font-headline text-2xl font-extrabold text-center text-on-surface">
               {txToDelete?.linked_type === "subscription" ? "Anular pago de suscripción" : "Eliminar Movimiento"}
             </DialogTitle>
           </DialogHeader>
           <div className="py-2 text-center text-base">
             <p className="text-on-surface-variant">
               ¿Deseas eliminar <strong>{txToDelete?.description || txToDelete?.category}</strong> por <strong>{txToDelete ? formatCurrency(txToDelete.amount) : ''}</strong>?
             </p>
             {txToDelete?.linked_type === "subscription" && <p className="mt-3 text-sm text-on-surface-variant">
               Se ajustará su cuenta o tarjeta. La suscripción y los demás pagos se conservan.
               Esta acción solo corrige el registro en Aorra; no solicita un reembolso.
               Después puedes revisar la próxima fecha de cobro en Suscripciones.
             </p>}
           </div>
           <div className="flex gap-3 pt-6">
             <Button 
               type="button" 
               variant="ghost" 
               className="flex-1 rounded-2xl h-14 font-extrabold text-base bg-surface-container-low hover:bg-surface-container text-on-surface transition-colors"
               onClick={() => setTxToDelete(null)}
               disabled={isDeleting}
             >
               Cancelar
             </Button>
             <Button 
               type="button" 
               className="flex-1 bg-rose-500 hover:bg-rose-600 text-white rounded-2xl h-14 font-extrabold text-base shadow-lg shadow-rose-500/20 transition-all"
               onClick={handleDelete}
               disabled={isDeleting}
             >
               {isDeleting ? <Loader2 className="w-5 h-5 animate-spin" /> : txToDelete?.linked_type === "subscription" ? "Anular pago" : "Eliminar"}
             </Button>
           </div>
         </DialogContent>
      </Dialog>
      
      <Dialog open={downloadDialogOpen} onOpenChange={setDownloadDialogOpen}>
         <DialogContent
           hideClose
           className="w-[calc(100%-32px)] sm:max-w-[560px] rounded-[32px] border-0 p-0 overflow-hidden"
           style={{ background: "#FFFFFF", boxShadow: "0 28px 90px rgba(21, 43, 87, 0.26)" }}
         >
           <div style={{ height: "6px", background: "linear-gradient(90deg, #2E65E6 0%, #31C0C1 52%, #42E695 100%)" }} />
           <div className="p-6 sm:p-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
             <button
               type="button"
               aria-label="Cerrar formulario de exportación"
               onClick={() => setDownloadDialogOpen(false)}
               className="absolute right-5 top-5 sm:right-6 sm:top-6 flex h-9 w-9 items-center justify-center rounded-full border-0 cursor-pointer"
               style={{ background: "#F1F5FF", color: "#5D6C89" }}
             ><X size={18} /></button>

             <DialogHeader className="space-y-0 text-left">
               <div className="flex items-center gap-4 pr-10">
                 <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl" style={{ background: "linear-gradient(145deg, #E5EEFF, #D7F8F3)", color: "#2E65E6" }}>
                   <Download size={26} strokeWidth={2.25} />
                 </div>
                 <div>
                   <div className="mb-1 text-[11px] font-extrabold uppercase tracking-[0.16em]" style={{ color: "#37AE9A" }}>Aorra reportes</div>
                   <DialogTitle className="text-[25px] font-extrabold tracking-[-0.04em]" style={{ color: "#203968" }}>Descargar extracto</DialogTitle>
                 </div>
               </div>
               <p className="mt-5 max-w-[420px] text-[14px] leading-6" style={{ color: "#6D7890" }}>Elige el período y descarga tus movimientos con el detalle que necesitas.</p>
             </DialogHeader>

             <div className="mt-7 rounded-[22px] p-4 sm:p-5" style={{ background: "#F6F8FF", border: "1px solid #E2E9FA" }}>
               <div className="mb-3 flex items-center justify-between gap-3">
                 <Label className="text-[13px] font-extrabold" style={{ color: "#405175" }}>Período rápido</Label>
                 {downloadDateRange.from && downloadDateRange.to && <span className="rounded-full px-2.5 py-1 text-[10px] font-extrabold" style={{ color: "#2E65E6", background: "#E2EAFE" }}>Listo para exportar</span>}
               </div>
               <div className="grid grid-cols-3 gap-2">
                 {[
                   ["this_month", "Este mes"],
                   ["last_month", "Mes anterior"],
                   ["this_year", "Este año"],
                 ].map(([key, label]) => (
                   <button
                     key={key}
                     type="button"
                     onClick={() => setQuickDateRange(key)}
                     className="min-h-11 rounded-xl border px-2 text-[12px] sm:text-[13px] font-bold transition-colors cursor-pointer"
                     style={{ background: "#FFFFFF", borderColor: "#DCE5F8", color: "#455675" }}
                     onMouseEnter={(event) => { event.currentTarget.style.borderColor = "#4D7BEC"; event.currentTarget.style.color = "#2E65E6"; }}
                     onMouseLeave={(event) => { event.currentTarget.style.borderColor = "#DCE5F8"; event.currentTarget.style.color = "#455675"; }}
                   >{label}</button>
                 ))}
               </div>
             </div>

             <div className="mt-5">
               <div className="mb-2.5 flex items-center justify-between">
                 <Label className="text-[13px] font-extrabold" style={{ color: "#405175" }}>Rango personalizado</Label>
                 {(downloadDateRange?.from || downloadDateRange?.to) && (
                   <button type="button" onClick={() => setDownloadDateRange({from:null,to:null})} className="border-0 bg-transparent text-[11px] font-extrabold cursor-pointer" style={{ color: "#EC6C79" }}>Limpiar</button>
                 )}
               </div>
               <Popover open={downloadDatePickerOpen} onOpenChange={setDownloadDatePickerOpen}>
                 <PopoverTrigger asChild>
                   <button type="button" className="flex h-[58px] w-full items-center gap-3 rounded-[18px] border px-4 text-left cursor-pointer" style={{ background: "#FFFFFF", borderColor: "#DCE5F8", color: "#203968" }}>
                     <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl" style={{ background: "#E8EFFF", color: "#2E65E6" }}><CalendarDays size={18} /></span>
                     <span className="min-w-0 flex-1 text-[13px] sm:text-[14px] font-bold">
                       {downloadDateRange.from && downloadDateRange.to ? `${downloadDateRange.from.toLocaleDateString('es-CO')} - ${downloadDateRange.to.toLocaleDateString('es-CO')}` : <span style={{ color: "#8A96AA" }}>Seleccionar fechas</span>}
                     </span>
                     <span className="text-[11px] font-extrabold" style={{ color: "#2E65E6" }}>Editar</span>
                   </button>
                 </PopoverTrigger>
                 <PopoverContent className="w-auto overflow-hidden rounded-3xl border-0 p-0 shadow-2xl" align="start" style={{ background: "#FFFFFF", boxShadow: "0 24px 48px rgba(23, 48, 99, 0.22)" }}>
                   <div className="p-2">
                     <Calendar
                       mode="range"
                       selected={downloadDateRange}
                       onSelect={(range) => { setDownloadDateRange(range || { from: null, to: null }); if(range?.from && range?.to) setDownloadDatePickerOpen(false); }}
                       numberOfMonths={1}
                       locale={{ localize: { month: n => MONTH_NAMES[n], day: n => ['D','L','M','X','J','V','S'][n] }, formatLong: { date: () => 'dd/MM/yyyy' } }}
                       classNames={{ root: "bg-transparent", months: "flex flex-col", month: "space-y-3", caption: "flex justify-center pt-3 pb-1 relative items-center px-4", caption_label: "text-sm font-bold text-on-surface font-headline tracking-wide", nav: "flex items-center gap-1", nav_button: "h-8 w-8 bg-primary/10 hover:bg-primary/20 text-primary rounded-xl flex items-center justify-center transition-colors cursor-pointer border-0 p-0", nav_button_previous: "absolute left-3", nav_button_next: "absolute right-3", table: "w-full border-collapse", head_row: "flex px-3 pb-1", head_cell: "text-outline rounded w-9 font-medium text-[0.7rem] flex-1 text-center pb-1", row: "flex w-full px-3 mt-1", cell: "flex-1 h-9 text-center text-sm relative focus-within:relative focus-within:z-20", day: "h-9 w-9 mx-auto flex items-center justify-center p-0 font-normal rounded-xl transition-all duration-150 text-sm hover:bg-primary/10 hover:text-primary cursor-pointer", day_range_middle: "rounded-none bg-primary/10 text-primary", day_range_start: "rounded-l-xl bg-primary text-on-primary font-bold shadow-sm", day_range_end: "rounded-r-xl bg-primary text-on-primary font-bold shadow-sm", day_selected: "bg-primary text-on-primary font-bold rounded-xl shadow-sm", day_today: "bg-tertiary-container text-on-tertiary-container font-bold", day_outside: "text-outline/40", day_disabled: "text-outline/30 cursor-not-allowed" }}
                     />
                   </div>
                 </PopoverContent>
               </Popover>
             </div>

             <div className="mt-7 grid grid-cols-2 gap-3">
               <button type="button" onClick={() => handleDownloadStatement('pdf')} disabled={downloading || !downloadDateRange.from || !downloadDateRange.to} className="flex h-[54px] items-center justify-center gap-2 rounded-2xl border-0 text-[14px] font-extrabold transition-opacity cursor-pointer disabled:cursor-not-allowed disabled:opacity-45" style={{ background: "#2E65E6", color: "#FFFFFF", boxShadow: "0 10px 20px rgba(46, 101, 230, 0.22)" }}>
                 {downloading ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <FileText className="h-[18px] w-[18px]" />} Descargar PDF
               </button>
               <button type="button" onClick={() => handleDownloadStatement('excel')} disabled={downloading || !downloadDateRange.from || !downloadDateRange.to} className="flex h-[54px] items-center justify-center gap-2 rounded-2xl border text-[14px] font-extrabold transition-colors cursor-pointer disabled:cursor-not-allowed disabled:opacity-45" style={{ background: "#ECFBF6", borderColor: "#C9EEE2", color: "#238F78" }}>
                 {downloading ? <Loader2 className="h-[18px] w-[18px] animate-spin" /> : <FileSpreadsheet className="h-[18px] w-[18px]" />} Descargar Excel
               </button>
             </div>
             <p className="mt-4 text-center text-[11px] font-medium" style={{ color: "#9AA4B6" }}>Tus archivos se generan de forma privada y segura.</p>
           </div>
         </DialogContent>
      </Dialog>
      <style>{`
        @media (max-width: 768px) {
          .aorra-transactions-summary {
            gap: 12px !important;
          }
        }
      `}</style>
    </div>
  );
};

export default TransactionsPage;
